import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { Banner, LocalizedText, DeploymentInfo } from '../services/banner/banner.model';
import { BannerService } from '../services/banner/banner.service';

@Component({
  selector: 'af-banner',
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly HEIGHT_VARIABLE = '--af-banner-height';

  private readonly bannerService = inject(BannerService);
  private readonly translate = inject(TranslateService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  private resizeObserver?: ResizeObserver;
  private measureCanvas?: HTMLCanvasElement;

  protected readonly banners = this.bannerService.activeBanners;
  protected readonly deploymentInfo: DeploymentInfo = this.bannerService.deploymentInfo;
  protected readonly deploymentText = signal('');



  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((event) => event.lang)),
    {
      initialValue:
        this.translate.currentLang() ?? this.translate.fallbackLang() ?? 'en',
    }
  );

  public ngOnInit(): void {
    this.bannerService
      .load()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    console.log(this.deploymentInfo);
  }

  public ngAfterViewInit(): void {
    // Publish the rendered banner height so full-viewport pages can subtract it
    // (see `--af-banner-height` usages) and avoid introducing a scrollbar.
    this.resizeObserver = new ResizeObserver(() => {
      this.publishHeight();
      this.updateDeploymentText();
    });
    this.resizeObserver.observe(this.host.nativeElement);
    this.publishHeight();
    this.updateDeploymentText();
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    document.documentElement.style.setProperty(
      BannerComponent.HEIGHT_VARIABLE,
      '0px'
    );
  }

  protected dismiss(id: string): void {
    this.bannerService.dismiss(id);
  }

  protected isDismissible(banner: Banner): boolean {
    return banner.dismissible !== false;
  }

  protected text(message: LocalizedText): string {
    if (typeof message === 'string') {
      return message;
    }
    const lang = this.currentLang() ?? 'en';
    return message[lang] ?? message['en'] ?? Object.values(message)[0] ?? '';
  }

  private publishHeight(): void {
    document.documentElement.style.setProperty(
      BannerComponent.HEIGHT_VARIABLE,
      `${this.host.nativeElement.offsetHeight}px`
    );
  }

  // Repeat the single deployment token enough times to fill the row; overflow is clipped in CSS.
  private updateDeploymentText(): void {
    if (!this.deploymentInfo.active) {
      return;
    }
    const container = this.host.nativeElement.querySelector<HTMLElement>(
      '.af-deployment'
    );
    const unit = this.text(this.deploymentInfo.name);
    if (!container || unit.length === 0) {
      this.deploymentText.set(unit);
      return;
    }
    const style = getComputedStyle(container);
    const canvas = (this.measureCanvas ??= document.createElement('canvas'));
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.deploymentText.set(unit);
      return;
    }
    ctx.font = `${style.fontSize} ${style.fontFamily}`;
    const unitWidth = ctx.measureText(unit).width;
    const available =
      container.clientWidth -
      parseFloat(style.paddingLeft) -
      parseFloat(style.paddingRight);
    const count =
      unitWidth > 0 ? Math.ceil(available / unitWidth) + 1 : 1;
    this.deploymentText.set(unit.repeat(Math.max(count, 1)));
  }
}
